import { useEffect, useRef, useCallback } from 'react';
import { useImmer } from 'use-immer';
import { message, notification } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { toggleLoginModal } from '@/reducers/login-pop';
import { setUID, setUserData } from '@/reducers/users';
import { setLocalStorageExpire } from '@/libs/utils/cookie-tool.js';
import useRequest from '@/hooks/useRequest';
import { getSign } from '@/api/util/get-sign.js';
import MD5 from 'crypto-js/md5';

const baseUrl = import.meta.env.VITE_AUTH;
const EXPIRES_TIME = 60 * 24 * 30;

const initialState = {
  // Modal
  showModal: false,

  // Tab
  loginTab: 'teacher',
  teacherMode: 'password',

  // Teacher form
  inputAccount: '',
  inputPassword: '',

  // Teacher code login
  teacherCode: '',
  teacherCodeCountdown: 0,
  teacherCodeSent: false,

  // Student form
  studentId: '',
  classId: '',

  // Error
  loginError: '',

  // Legacy verify / forgot password
  isVerifyLogin: false,
  isForgotPassword: false,
  showVerifyCode: false,
  phoneNumber: '',
  verifyCode: ['', '', '', '', '', ''],
  verifyResult: null,

  // Reset password
  newPassword: '',
  confirmPassword: '',
  passwordError: '',
  newPasswordUid: '',

  // Countdown
  countdown: 0,
};

export const useLoginModal = () => {
  const dispatch = useDispatch();
  const isOpen = useSelector((state) => state.loginPop.isLoginModalOpen);

  const { loadingStates, sendRequest } = useRequest();
  const verifyInputRefs = useRef([]);
  const animationFrameRef = useRef(null);

  const [state, setState] = useImmer(initialState);

  const update = useCallback(
    (updates) => {
      setState((draft) => {
        Object.assign(draft, updates);
      });
    },
    [setState],
  );

  const resetAllForms = useCallback(() => {
    setState((draft) => {
      Object.assign(draft, initialState);
    });
  }, [setState]);

  const resetLoginForm = useCallback(() => {
    setState((draft) => {
      draft.inputAccount = '';
      draft.inputPassword = '';
      draft.studentId = '';
      draft.classId = '';
      draft.teacherCode = '';
      draft.loginError = '';
    });
  }, [setState]);

  // Modal open/close animation
  useEffect(() => {
    if (isOpen) {
      animationFrameRef.current = requestAnimationFrame(() => {
        setState((draft) => {
          draft.showModal = true;
        });
      });
    } else {
      setState((draft) => {
        draft.showModal = false;
      });
      resetAllForms();
    }
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [isOpen, resetAllForms, setState]);

  const handleClose = useCallback(() => {
    dispatch(toggleLoginModal(false));
  }, [dispatch]);

  const switchTab = useCallback(
    (tab) => {
      setState((draft) => {
        draft.loginTab = tab;
        draft.loginError = '';
      });
    },
    [setState],
  );

  const switchTeacherMode = useCallback(
    (mode) => {
      setState((draft) => {
        draft.teacherMode = mode;
        draft.loginError = '';
      });
    },
    [setState],
  );

  // Utils
  const getCurrentDateTime = useCallback(() => {
    const now = new Date();
    const utc = now.getTime() + now.getTimezoneOffset() * 60000;
    const beijingTime = new Date(utc + 3600000 * 8);
    const year = beijingTime.getFullYear();
    const month = String(beijingTime.getMonth() + 1).padStart(2, '0');
    const day = String(beijingTime.getDate()).padStart(2, '0');
    const hour = String(beijingTime.getHours()).padStart(2, '0');
    const minute = String(beijingTime.getMinutes()).padStart(2, '0');
    return `${year}${month}${day}${hour}${minute}`;
  }, []);

  const generateMD5 = useCallback((str) => {
    return MD5(str).toString();
  }, []);

  // After login success
  const completeLoginProcess = useCallback(
    async (loginData) => {
      try {
        const { uuid } = loginData;
        const loginResponse = await sendRequest(
          {
            method: 'POST',
            url: `/api/login?uuid=${uuid}`,
          },
          'loginZMUid',
        );

        if (loginResponse.status === 1) {
          setLocalStorageExpire('uid', loginResponse.data.uid, EXPIRES_TIME);
          dispatch(setUID(loginResponse.data.uid));
        }

        const userDataForm = new FormData();
        userDataForm.append('secret', 'zmrobo');
        const loginUserData = await sendRequest({
          method: 'POST',
          url: '/api/user/getUserInfo',
          data: userDataForm,
        });

        if (loginUserData.status === 1) {
          message.success('登录成功');
          dispatch(setUserData(loginUserData.data));
          dispatch(toggleLoginModal(false));
        } else {
          throw new Error(loginUserData.message);
        }
      } catch (error) {
        console.error('登录过程中出现错误:', error);
      }
    },
    [dispatch, sendRequest],
  );

  const handleLoginSuccess = useCallback(
    async (uid) => {
      setLocalStorageExpire('uid', uid, EXPIRES_TIME);
      dispatch(setUID(uid));

      const userDataForm = new FormData();
      userDataForm.append('secret', 'zmrobo');
      const loginUserData = await sendRequest({
        method: 'POST',
        url: '/api/user/getUserInfo',
        data: userDataForm,
      });

      if (loginUserData.status !== 1) {
        throw new Error(loginUserData.message);
      }

      message.success('登录成功');
      dispatch(setUserData(loginUserData.data));
      dispatch(toggleLoginModal(false));
    },
    [dispatch, sendRequest],
  );

  // Countdown
  const startCountdown = useCallback(() => {
    setState((draft) => {
      draft.countdown = 60;
    });
    const timer = setInterval(() => {
      setState((draft) => {
        if (draft.countdown <= 1) {
          clearInterval(timer);
          draft.countdown = 0;
        } else {
          draft.countdown -= 1;
        }
      });
    }, 1000);
  }, [setState]);

  const startTeacherCodeCountdown = useCallback(() => {
    setState((draft) => {
      draft.teacherCodeCountdown = 60;
      draft.teacherCodeSent = true;
    });
    const timer = setInterval(() => {
      setState((draft) => {
        if (draft.teacherCodeCountdown <= 1) {
          clearInterval(timer);
          draft.teacherCodeCountdown = 0;
        } else {
          draft.teacherCodeCountdown -= 1;
        }
      });
    }, 1000);
  }, [setState]);

  // Password validation
  const validatePassword = useCallback(
    (password, confirmPwd) => {
      let error = '';
      if (!password) {
        error = '请输入密码';
      } else if (password.length < 8 || password.length > 12) {
        error = '密码长度必须在8-12位之间';
      } else if (!/^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d]{8,12}$/.test(password)) {
        error = '密码必须包含数字和字母';
      } else if (confirmPwd && password !== confirmPwd) {
        error = '两次输入的密码不一致';
      }
      setState((draft) => {
        draft.passwordError = error;
      });
      return !error;
    },
    [setState],
  );

  // Send verify code (legacy flow)
  const handleSendVerifyCode = useCallback(async () => {
    try {
      const formData = new FormData();
      const currentTime = new Date();
      const utcDate = currentTime.getTime();
      const emailReg = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

      if (emailReg.test(state.phoneNumber)) {
        formData.append('email', state.phoneNumber);
        formData.append('language', 'zh-CN');
        const sign = getSign('client/verify/mail', 'POST', utcDate);
        const sendEmail = await sendRequest(
          {
            method: 'POST',
            url: `${baseUrl}/client/verify/mail`,
            headers: { 'C-Date': utcDate, Sign: sign },
            data: formData,
          },
          'sendEmail',
        );

        if (sendEmail.code === 0) {
          setState((draft) => {
            draft.showVerifyCode = true;
          });
          startCountdown();
          setTimeout(() => verifyInputRefs.current[0]?.focus(), 0);
        } else if (sendEmail.code === -4 || sendEmail.code === -5) {
          notification.error({ message: '操作频繁，请稍后再试' });
        } else if (sendEmail.code === -2) {
          notification.error({ message: '输入的邮箱有误' });
        } else {
          notification.error({ message: sendEmail.msg });
        }
      } else {
        formData.append('mobile', state.phoneNumber);
        formData.append('sms_type', '86');
        const sign = getSign('client/verify/sms', 'POST', utcDate);
        const sendCode = await sendRequest(
          {
            method: 'POST',
            url: `${baseUrl}/client/verify/sms`,
            headers: { 'C-Date': utcDate, Sign: sign },
            data: formData,
          },
          'sendCode',
        );

        if (sendCode.code === 0) {
          setState((draft) => {
            draft.showVerifyCode = true;
          });
          startCountdown();
          setTimeout(() => verifyInputRefs.current[0]?.focus(), 0);
        } else if (sendCode.code === -4) {
          notification.error({ message: '操作频繁，请稍后再试' });
        } else if (sendCode.code === -2) {
          notification.error({ message: '输入的手机号有误' });
        } else {
          notification.error({ message: sendCode.msg });
        }
      }
    } catch (error) {
      console.error('发送验证码失败:', error);
    }
  }, [state.phoneNumber, sendRequest, startCountdown, setState]);

  // Send code for teacher code login
  const handleSendTeacherCode = useCallback(async () => {
    try {
      const formData = new FormData();
      const currentTime = new Date();
      const utcDate = currentTime.getTime();
      const emailReg = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

      if (emailReg.test(state.inputAccount)) {
        formData.append('email', state.inputAccount);
        formData.append('language', 'zh-CN');
        const sign = getSign('client/verify/mail', 'POST', utcDate);
        const sendEmail = await sendRequest(
          {
            method: 'POST',
            url: `${baseUrl}/client/verify/mail`,
            headers: { 'C-Date': utcDate, Sign: sign },
            data: formData,
          },
          'sendTeacherCode',
        );

        if (sendEmail.code === 0) {
          startTeacherCodeCountdown();
        } else if (sendEmail.code === -4 || sendEmail.code === -5) {
          notification.error({ message: '操作频繁，请稍后再试' });
        } else if (sendEmail.code === -2) {
          notification.error({ message: '输入的邮箱有误' });
        } else {
          notification.error({ message: sendEmail.msg });
        }
      } else {
        formData.append('mobile', state.inputAccount);
        formData.append('sms_type', '86');
        const sign = getSign('client/verify/sms', 'POST', utcDate);
        const sendCode = await sendRequest(
          {
            method: 'POST',
            url: `${baseUrl}/client/verify/sms`,
            headers: { 'C-Date': utcDate, Sign: sign },
            data: formData,
          },
          'sendTeacherCode',
        );

        if (sendCode.code === 0) {
          startTeacherCodeCountdown();
        } else if (sendCode.code === -4) {
          notification.error({ message: '操作频繁，请稍后再试' });
        } else if (sendCode.code === -2) {
          notification.error({ message: '输入的手机号有误' });
        } else {
          notification.error({ message: sendCode.msg });
        }
      }
    } catch (error) {
      console.error('发送验证码失败:', error);
    }
  }, [state.inputAccount, sendRequest, startTeacherCodeCountdown, setState]);

  // Verify code check (legacy 6-digit)
  const performVerifyCodeCheck = useCallback(
    async (fullCode) => {
      try {
        const formData = new FormData();
        const currentTime = new Date();
        const utcDate = currentTime.getTime();
        const emailReg = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        const valueData = emailReg.test(state.phoneNumber) ? 'email' : 'mobile';
        formData.append('account', state.phoneNumber);
        formData.append('auth_type', valueData);
        formData.append('code', fullCode);

        if (state.isForgotPassword) {
          formData.append('forget', MD5('1').toString());
        }

        const sign = getSign('client/verify/login', 'POST', utcDate);
        const checkCode = await sendRequest({
          method: 'POST',
          url: `${baseUrl}/client/verify/login`,
          headers: { 'C-Date': utcDate, Sign: sign },
          data: formData,
        });

        if (checkCode.code === 0) {
          if (state.isForgotPassword) {
            setState((draft) => {
              draft.verifyResult = 'reset_password';
              draft.newPasswordUid = checkCode.data.uuid;
            });
          } else if (checkCode.data.passwordStatus === 0) {
            setState((draft) => {
              draft.verifyResult = 'reset_password';
              draft.newPasswordUid = checkCode.data.uuid;
            });
          } else if (checkCode.data.passwordStatus === 1) {
            setState((draft) => {
              draft.verifyResult = 'success';
            });
            await completeLoginProcess({ uuid: checkCode.data.uuid });
          }
        } else {
          notification.error({ message: '验证码错误' });
          setTimeout(() => {
            setState((draft) => {
              draft.verifyResult = 'fail';
            });
          }, 3000);
        }
      } catch (error) {
        console.error('验证码验证失败:', error);
        setState((draft) => {
          draft.verifyResult = 'fail';
        });
      }
    },
    [
      state.phoneNumber,
      state.isForgotPassword,
      sendRequest,
      completeLoginProcess,
      setState,
    ],
  );

  // Legacy 6-digit code input handlers
  const handleVerifyCodeChange = useCallback(
    async (index, value) => {
      if (!/^\d*$/.test(value)) return;

      const newVerifyCode = [...state.verifyCode];
      newVerifyCode[index] = value;
      setState((draft) => {
        draft.verifyCode = newVerifyCode;
      });

      if (value.length === 1 && index < 5) {
        setTimeout(() => verifyInputRefs.current[index + 1]?.focus(), 0);
      }

      if (index === 5 && value) {
        await performVerifyCodeCheck(newVerifyCode.join(''));
      }
    },
    [state.verifyCode, performVerifyCodeCheck, setState],
  );

  const handleVerifyCodeKeyDown = useCallback(
    (index, e) => {
      if (e.key === 'Backspace' && !state.verifyCode[index] && index > 0) {
        verifyInputRefs.current[index - 1]?.focus();
      }
    },
    [state.verifyCode],
  );

  const handleVerifyCodePaste = useCallback(
    async (e) => {
      e.preventDefault();
      const pastedData = e.clipboardData.getData('text').slice(0, 6);
      if (!/^\d*$/.test(pastedData)) return;

      const newVerifyCode = [...state.verifyCode];
      for (let i = 0; i < pastedData.length; i++) {
        newVerifyCode[i] = pastedData[i];
      }
      setState((draft) => {
        draft.verifyCode = newVerifyCode;
      });

      if (pastedData.length === 6) {
        await performVerifyCodeCheck(pastedData);
      }
    },
    [state.verifyCode, performVerifyCodeCheck, setState],
  );

  // Perform login request
  const performLogin = useCallback(
    async (isStudent, valueData) => {
      const encryptedPassword = generateMD5(state.inputPassword);

      if (!isStudent) {
        const formData = new FormData();
        formData.append('account', state.inputAccount);
        formData.append('password', encryptedPassword);
        formData.append('auth_type', valueData);

        return await sendRequest(
          {
            method: 'POST',
            url: '/api/login_zm',
            data: formData,
          },
          'loginZMUid',
        );
      } else {
        const loginStuForm = new FormData();
        loginStuForm.append('account', state.inputAccount);
        loginStuForm.append('password', encryptedPassword);

        const currentDateTime = getCurrentDateTime();
        const secretStr = `zmrobo#${currentDateTime}`;
        const encryptedSecret = generateMD5(secretStr);
        loginStuForm.append('secret', encryptedSecret);

        return await sendRequest(
          {
            method: 'POST',
            url: '/api/login_stu',
            data: loginStuForm,
          },
          'loginSTU',
        );
      }
    },
    [
      state.inputAccount,
      state.inputPassword,
      generateMD5,
      getCurrentDateTime,
      sendRequest,
    ],
  );

  // Teacher code login
  const performTeacherCodeLogin = useCallback(async () => {
    try {
      const formData = new FormData();
      const currentTime = new Date();
      const utcDate = currentTime.getTime();
      const emailReg = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      const valueData = emailReg.test(state.inputAccount) ? 'email' : 'mobile';

      formData.append('account', state.inputAccount);
      formData.append('auth_type', valueData);
      formData.append('code', state.teacherCode);

      const sign = getSign('client/verify/login', 'POST', utcDate);
      const checkCode = await sendRequest(
        {
          method: 'POST',
          url: `${baseUrl}/client/verify/login`,
          headers: { 'C-Date': utcDate, Sign: sign },
          data: formData,
        },
        'teacherCodeLogin',
      );

      if (checkCode.code === 0) {
        await completeLoginProcess({ uuid: checkCode.data.uuid });
      } else {
        setState((draft) => {
          draft.loginError = '验证码错误';
        });
      }
    } catch (error) {
      console.error('验证码登录失败:', error);
      setState((draft) => {
        draft.loginError = '登录失败，请重试';
      });
    }
  }, [
    state.inputAccount,
    state.teacherCode,
    sendRequest,
    completeLoginProcess,
    setState,
  ]);

  // Main login entry
  const onLogin = useCallback(async () => {
    setState((draft) => {
      draft.loginError = '';
    });

    try {
      if (state.loginTab === 'student') {
        const loginStuForm = new FormData();
        loginStuForm.append('stu_id', state.studentId);
        loginStuForm.append('class_num', state.classId);
        const currentDateTime = getCurrentDateTime();
        const secretStr = `zmrobo#${currentDateTime}`;
        loginStuForm.append('secret', generateMD5(secretStr));

        const loginResponse = await sendRequest(
          {
            method: 'POST',
            url: '/api/login_stu_num',
            data: loginStuForm,
          },
          'loginSTU',
        );

        if (loginResponse.status !== 1) {
          setState((draft) => {
            draft.loginError = loginResponse.message || '账号或密码错误';
          });
          return;
        }
        await handleLoginSuccess(loginResponse.data.uid);
        return;
      }

      if (state.loginTab === 'teacher') {
        if (state.teacherMode === 'code') {
          if (!state.teacherCode.trim()) {
            setState((draft) => {
              draft.loginError = '请输入验证码';
            });
            return;
          }
          await performTeacherCodeLogin();
          return;
        }

        const emailReg = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        const valueData = emailReg.test(state.inputAccount)
          ? 'email'
          : 'mobile';
        const isStudent = /^[^.]*@[^.]*$/.test(state.inputAccount);

        const loginResponse = await performLogin(isStudent, valueData);
        if (loginResponse.status !== 1) {
          setState((draft) => {
            draft.loginError = isStudent
              ? loginResponse.message
              : '账号或密码错误';
          });
          return;
        }
        await handleLoginSuccess(loginResponse.data.uid);
      }
    } catch (error) {
      console.error('登录过程中出现错误:', error);
      setState((draft) => {
        draft.loginError = '登录失败，请重试';
      });
    }
  }, [
    state.loginTab,
    state.teacherMode,
    state.inputAccount,
    state.inputPassword,
    state.studentId,
    state.teacherCode,
    performLogin,
    performTeacherCodeLogin,
    handleLoginSuccess,
    generateMD5,
    getCurrentDateTime,
    sendRequest,
    setState,
  ]);

  // Reset password
  const handleResetPassword = useCallback(async () => {
    if (!validatePassword(state.newPassword, state.confirmPassword)) return;

    try {
      const formData = new FormData();
      const currentTime = new Date();
      const utcDate = currentTime.getTime();
      formData.append('uuid', state.newPasswordUid);
      formData.append('password', state.newPassword);
      const sign = getSign('client/verify/password', 'POST', utcDate);
      const newPasswordResponse = await sendRequest(
        {
          method: 'POST',
          url: `${baseUrl}/client/verify/password`,
          headers: { 'C-Date': utcDate, Sign: sign },
          data: formData,
        },
        'resetPassword',
      );

      if (newPasswordResponse.code === 0) {
        message.success('密码重置成功');
        await completeLoginProcess({ uuid: state.newPasswordUid });
      } else {
        setState((draft) => {
          draft.passwordError = '密码重置失败，请重试';
        });
      }
    } catch (error) {
      console.error('重置密码失败:', error);
      setState((draft) => {
        draft.passwordError = '密码重置失败，请重试';
      });
    }
  }, [
    state.newPassword,
    state.confirmPassword,
    state.newPasswordUid,
    validatePassword,
    sendRequest,
    completeLoginProcess,
    setState,
  ]);

  // Legacy navigation
  const goToForgotPassword = useCallback(() => {
    setState((draft) => {
      draft.isForgotPassword = true;
      draft.isVerifyLogin = true;
    });
  }, [setState]);

  const goToVerifyLogin = useCallback(() => {
    setState((draft) => {
      draft.isVerifyLogin = true;
    });
  }, [setState]);

  // Unified return object
  return {
    // Read-only state
    state,

    // Actions
    update,
    handleClose,
    switchTab,
    switchTeacherMode,
    handleSendTeacherCode,
    onLogin,
    resetLoginForm,
    handleVerifyCodeChange,
    handleVerifyCodeKeyDown,
    handleVerifyCodePaste,
    handleSendVerifyCode,
    handleResetPassword,
    validatePassword,
    goToForgotPassword,
    goToVerifyLogin,

    // Refs
    verifyInputRefs,

    // External data
    loadingStates,
    isOpen,
  };
};

export default useLoginModal;
